import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Slash, Trash2, X } from 'react-feather';
import {
  puedeGestionarRespuestas,
  tiempoRelativo,
  useBloqueos,
  useCrearBloqueo,
  useEliminarBloqueo,
} from '../../features/tickets';
import { getItem, STORAGE_KEYS } from '../../services/storage';

// Mismo minimo que el backend: un patron de 1-2 letras casaria con casi
// cualquier correo y vaciaria el buzon en silencio.
const MIN_PATRON = 3;

const TIPOS = {
  remitente: {
    etiqueta: 'Remitente',
    opcion: 'El remitente contiene',
    placeholder: 'Ej. fivetran.com o notificaciones@proveedor.com',
  },
  asunto: {
    etiqueta: 'Asunto',
    opcion: 'El asunto contiene',
    placeholder: 'Ej. Delivery Status Notification',
  },
};

const errorBackend = (error, fallback) => error?.response?.data?.error || fallback;

const TicketsBloqueos = ({ headquarter }) => {
  const email = useMemo(() => getItem(STORAGE_KEYS.USER)?.profileObj?.email || '', []);
  // Misma lista de autorizados que las respuestas guardadas (el backend la valida).
  const puedeGestionar = puedeGestionarRespuestas(email);

  const { data: bloqueos = [], isLoading, isError, refetch } = useBloqueos(headquarter);
  const crear = useCrearBloqueo(headquarter);
  const eliminar = useEliminarBloqueo(headquarter);

  const [tipo, setTipo] = useState('remitente');
  const [patron, setPatron] = useState('');
  const [motivo, setMotivo] = useState('');
  const [formError, setFormError] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const onCrear = (e) => {
    e.preventDefault();
    if (patron.trim().length < MIN_PATRON) {
      setFormError(`El texto a bloquear debe tener al menos ${MIN_PATRON} caracteres.`);
      return;
    }
    setFormError('');
    crear.mutate(
      { tipo, patron: patron.trim(), motivo: motivo.trim() || undefined },
      {
        onSuccess: () => {
          setPatron('');
          setMotivo('');
          setFeedback({ tipo: 'exito', texto: 'Bloqueo añadido.' });
        },
        onError: (error) =>
          setFeedback({
            tipo: 'error',
            texto: errorBackend(error, 'No se pudo añadir el bloqueo.'),
          }),
      },
    );
  };

  const confirmarBorrado = (id) => {
    eliminar.mutate(id, {
      onSuccess: () => {
        setConfirmId(null);
        setFeedback({ tipo: 'exito', texto: 'Bloqueo quitado.' });
      },
      onError: (error) => {
        setConfirmId(null);
        setFeedback({ tipo: 'error', texto: errorBackend(error, 'No se pudo quitar el bloqueo.') });
      },
    });
  };

  return (
    <section className="tickets-panel tickets-respuestas tickets-bloqueos">
      <header className="tickets-listhead">
        <div>
          <h1 className="tickets-listhead__title">Correos bloqueados</h1>
          <p className="tickets-listhead__sub">
            Los correos que coinciden con estas reglas no crean ticket en ninguna bandeja. No se
            distinguen mayúsculas y los cambios se aplican en la siguiente lectura del buzón.
          </p>
        </div>
      </header>

      {feedback && (
        <div
          className={`tickets-respuestas__feedback tickets-respuestas__feedback--${feedback.tipo}`}
          role="status"
        >
          <span>{feedback.texto}</span>
          <button
            type="button"
            className="tickets-tag__remove"
            aria-label="Cerrar aviso"
            onClick={() => setFeedback(null)}
          >
            <X size={14} aria-hidden="true" />
          </button>
        </div>
      )}

      {puedeGestionar && (
        <form className="tickets-respuestas__form" onSubmit={onCrear}>
          <div className="tickets-bloqueos__campos">
            <div className="tickets-respuestas__field">
              <label className="tickets-props__label" htmlFor="bloqueo-tipo">
                Bloquear si…
              </label>
              <select
                id="bloqueo-tipo"
                className="tickets-input"
                value={tipo}
                onChange={(e) => setTipo(e.target.value)}
                disabled={crear.isPending}
              >
                {Object.entries(TIPOS).map(([valor, t]) => (
                  <option key={valor} value={valor}>
                    {t.opcion}
                  </option>
                ))}
              </select>
            </div>
            <div className="tickets-respuestas__field tickets-bloqueos__patron">
              <label className="tickets-props__label" htmlFor="bloqueo-patron">
                Texto
              </label>
              <input
                id="bloqueo-patron"
                type="text"
                className="tickets-input"
                value={patron}
                onChange={(e) => setPatron(e.target.value)}
                placeholder={TIPOS[tipo].placeholder}
                maxLength={190}
                disabled={crear.isPending}
              />
            </div>
          </div>
          <div className="tickets-respuestas__field">
            <label className="tickets-props__label" htmlFor="bloqueo-motivo">
              Motivo (opcional)
            </label>
            <input
              id="bloqueo-motivo"
              type="text"
              className="tickets-input"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ej. Notificaciones automáticas de sincronización"
              maxLength={255}
              disabled={crear.isPending}
            />
          </div>
          {formError && <p className="tickets-composer__error">{formError}</p>}
          <div className="tickets-respuestas__form-actions">
            <button
              type="submit"
              className="tickets-btn tickets-btn--primary"
              disabled={crear.isPending}
            >
              <Slash size={16} aria-hidden="true" />
              {crear.isPending ? 'Guardando…' : 'Bloquear'}
            </button>
          </div>
        </form>
      )}

      {isError ? (
        <div className="tickets-empty">
          No se pudieron cargar los bloqueos.
          <div className="tickets-respuestas__retry">
            <button
              type="button"
              className="tickets-btn tickets-btn--secondary"
              onClick={() => refetch()}
            >
              Reintentar
            </button>
          </div>
        </div>
      ) : isLoading ? (
        <div className="tickets-empty">Cargando bloqueos…</div>
      ) : bloqueos.length === 0 ? (
        <div className="tickets-empty">No hay ningún correo bloqueado.</div>
      ) : (
        <ul className="tickets-respuestas__list">
          {bloqueos.map((item) => (
            <li key={item.id} className="tickets-respuestas__item">
              <div className="tickets-respuestas__item-head">
                <div className="tickets-bloqueos__regla">
                  <span className={`tickets-bloqueos__tipo tickets-bloqueos__tipo--${item.tipo}`}>
                    {TIPOS[item.tipo]?.etiqueta || item.tipo}
                  </span>
                  <span className="tickets-bloqueos__contiene">contiene</span>
                  <code className="tickets-bloqueos__patron-texto">{item.patron}</code>
                </div>
                {puedeGestionar && confirmId !== item.id && (
                  <div className="tickets-respuestas__item-actions">
                    <button
                      type="button"
                      className="tickets-btn tickets-btn--ghost"
                      onClick={() => setConfirmId(item.id)}
                    >
                      <Trash2 size={14} aria-hidden="true" />
                      Quitar
                    </button>
                  </div>
                )}
              </div>
              {item.motivo && <p className="tickets-respuestas__item-cuerpo">{item.motivo}</p>}
              <p className="tickets-respuestas__item-meta">
                {item.creadoPor ? `Añadido por ${item.creadoPor}` : 'Añadido'}
                {item.fechaAlta ? ` · ${tiempoRelativo(item.fechaAlta)}` : ''}
              </p>
              {puedeGestionar && confirmId === item.id && (
                <div className="tickets-respuestas__confirm">
                  <span>¿Quitar este bloqueo? Esos correos volverán a crear tickets.</span>
                  <div className="tickets-respuestas__item-actions">
                    <button
                      type="button"
                      className="tickets-btn tickets-btn--ghost"
                      onClick={() => setConfirmId(null)}
                      disabled={eliminar.isPending}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      className="tickets-btn tickets-btn--danger"
                      onClick={() => confirmarBorrado(item.id)}
                      disabled={eliminar.isPending}
                    >
                      <Trash2 size={14} aria-hidden="true" />
                      {eliminar.isPending ? 'Quitando…' : 'Quitar bloqueo'}
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

TicketsBloqueos.propTypes = {
  headquarter: PropTypes.string.isRequired,
};

export default TicketsBloqueos;
